import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { authAPI } from '../services/api';
import type { User, LoginCredentials, RegisterData, AuthContextType } from '../types/auth';
import { clearAppliedTimeIds } from '../state/timeCandidaturas';
import { clearAppliedSubstituicaoIds } from '../state/substituicaoCandidaturas';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));

      // Revalida os dados do usuário com o servidor para garantir nome/foto atualizados
      authAPI.me()
        .then((res) => {
          const freshUser = res.data.user;
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        })
        .catch(() => {
          // Token expirado ou inválido — limpa a sessão
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        });
    }
    setLoading(false);
  }, []);

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      setLoading(true);
      const response = await authAPI.login(credentials);

      const { user, token } = response.data;

      setUser(user);
      setToken(token);

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Não mostrar toast aqui - deixar para o componente Login gerenciar
    } catch (error: any) {
      // Não mostrar toast de erro aqui - deixar para o componente Login gerenciar
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (data: RegisterData): Promise<void> => {
    try {
      setLoading(true);
      const response = await authAPI.register(data);

      const { user, token } = response.data;

      setUser(user);
      setToken(token);

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      toast.success('Conta criada com sucesso!');
    } catch (error: any) {
      console.error('Register error:', error);
      const message = error.response?.data?.message || 'Erro ao criar conta. Tente novamente.';
      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Rebusca /api/auth/me e sincroniza contexto + localStorage — chamado depois de
  // uploads de foto de perfil/logo pra refletir de imediato em telas que leem do
  // contexto (ex.: avatar do sidebar), que senão só atualizariam no próximo login.
  const refreshUser = async () => {
    try {
      const res = await authAPI.me();
      const freshUser = res.data.user;
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch (error) {
      console.error('Erro ao atualizar dados do usuário:', error);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Higiene extra: as marcas de "já se candidatou" já são isoladas por e-mail,
      // mas limpamos aqui também para não deixar rastro de sessão encerrada.
      if (user?.email) {
        clearAppliedTimeIds(user.email);
        clearAppliedSubstituicaoIds(user.email);
      }
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};